import AttorneyHubAPIService from "common/services/api/attorney-hub-api-service";
import Guid from "common/values/guid/guid";
import axios, { AxiosHeaders, CanceledError } from "axios";
import Session from "users/session/session";
import NetworkedGroupQuery from "users/entities/networked-group/api/request-contracts/query-networked-groups-api-request";
import NetworkedGroupApiResponse from "users/entities/networked-group/api/response-contracts/networked-group-api-response";
import PaginatedResponse from "common/contracts/paginated-response";
import NetworkedGroup from "users/entities/networked-group/networked-group";
import CreateNetworkedGroupApiRequest from "users/entities/networked-group/api/request-contracts/create-networked-group-api-request";
import UpdateNetworkedGroupApiRequest from "users/entities/networked-group/api/request-contracts/update-networked-group-api-request";


export default class NetworkedGroupAPIService {
  private readonly _authHeaders: AxiosHeaders = new AxiosHeaders();
  private readonly _session: Readonly<Session>;

  constructor(session: Readonly<Session>) {
    this._session = session;
    if (!session.authToken?.value)
      throw new Error("Session must have an authToken to create a NetworkedGroupAPIService");
    this._authHeaders.set("Authorization", `Bearer ${session.authToken.value}`);
  }

  async getNetworkedGroupById(id: Guid, abortController?: AbortController): Promise<NetworkedGroup> {
    try {
      const response = await axios.get(
        `${AttorneyHubAPIService.apiBaseUrl}/users/networked-groups/${id.value}`,
        { headers: this._authHeaders, signal: abortController?.signal }
      );
      return new NetworkedGroupApiResponse(response.data).deserialize();
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.code === "ERR_CANCELED") {
        throw new CanceledError("Request was canceled");
      }
      throw error;
    }
  }

  async queryNetworkedGroups(
    query: NetworkedGroupQuery,
    abortController?: AbortController
  ): Promise<PaginatedResponse<NetworkedGroup>> {
    try {
      const url = new URL(
        `/users/networked-groups?${query.asSearchParams().toString()}`,
        AttorneyHubAPIService.apiBaseUrl
      );
      const response = await axios.get(url.toString(), {
        headers: this._authHeaders,
        signal: abortController?.signal,
      });
      const data = response.data.map((item: Partial<NetworkedGroupApiResponse>) =>
        new NetworkedGroupApiResponse(item).deserialize()
      );
      return PaginatedResponse.from<NetworkedGroup>(
        data,
        response.headers['x-pagination']
      );
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.code === "ERR_CANCELED") {
        throw new CanceledError("Request was canceled");
      }
      throw error;
    }
  }

  async createNetworkedGroup(
    networkedGroup: CreateNetworkedGroupApiRequest,
    abortController?: AbortController
  ): Promise<NetworkedGroup> {
    try {
      const response = await axios.post(
        `${AttorneyHubAPIService.apiBaseUrl}/users/networked-groups`,
        networkedGroup,
        { headers: this._authHeaders, signal: abortController?.signal }
      );
      return new NetworkedGroupApiResponse(response.data).deserialize();
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.code === "ERR_CANCELED") {
        throw new CanceledError("Request was canceled");
      }
      throw error;
    }
  }

  async updateNetworkedGroup(
    id: Guid,
    request: UpdateNetworkedGroupApiRequest,
    abortController?: AbortController
  ): Promise<NetworkedGroup> {
    try {
      const response = await axios.patch(
        `${AttorneyHubAPIService.apiBaseUrl}/users/networked-groups/${id.value}`,
        request.payload,
        {
          headers: this._authHeaders.concat({ "Content-Type": "application/json" }),
          signal: abortController?.signal
        }
      );
      return new NetworkedGroupApiResponse(response.data).deserialize();
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.code === "ERR_CANCELED") {
        throw new CanceledError("Request was canceled");
      }
      throw error;
    }
  }
}
